import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

// Plan pricing configuration types
export interface PlanPricingConfig {
  id: number;
  plan_code: string;
  plan_name: string;
  monthly_price_paise: number;
  annual_price_paise: number;
  annual_discount_percent: number;
  monthly_credit_grant: number;
  addon_credit_price: number;
  razorpay_key_id?: string;
  features: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PlanPricingUpdate {
  planName?: string;
  monthlyPricePaise?: number;
  annualPricePaise?: number;
  annualDiscountPercent?: number;
  monthlyCreditGrant?: number;
  addonCreditPrice?: number;
  razorpayKeyId?: string;
  features?: string[];
}

export interface PlanPricingCreate extends PlanPricingUpdate {
  planCode: string;
  planName: string;
}

// Hook to get all plan pricing configurations
export function usePlanPricingConfigs() {
  return useQuery({
    queryKey: ["/api/admin/plan-pricing-configs"],
    queryFn: async () => {
      const response = await apiRequest("/api/admin/plan-pricing-configs", "GET");
      return (await response.json()) as PlanPricingConfig[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

// Hook to update a plan pricing configuration
export function useUpdatePlanPricing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ planCode, pricingData }: { planCode: string; pricingData: PlanPricingUpdate }) => {
      const response = await apiRequest(`/api/admin/plan-pricing-configs/${planCode}`, "PUT", pricingData);
      return (await response.json()) as PlanPricingConfig;
    },
    onSuccess: () => {
      // Invalidate and refetch plan pricing configs
      queryClient.invalidateQueries({ queryKey: ["/api/admin/plan-pricing-configs"] });
      // Also invalidate the public billing plans endpoint
      queryClient.invalidateQueries({ queryKey: ["/api/billing/plans"] });
    },
    onError: (error) => {
      console.error("Error updating plan pricing:", error);
    },
  });
}

// Hook to create a new plan pricing configuration
export function useCreatePlanPricing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pricingData: PlanPricingCreate) => {
      const response = await apiRequest("/api/admin/plan-pricing-configs", "POST", pricingData);
      return (await response.json()) as PlanPricingConfig;
    },
    onSuccess: () => {
      // Invalidate and refetch plan pricing configs
      queryClient.invalidateQueries({ queryKey: ["/api/admin/plan-pricing-configs"] });
      // Also invalidate the public billing plans endpoint
      queryClient.invalidateQueries({ queryKey: ["/api/billing/plans"] });
    },
    onError: (error) => {
      console.error("Error creating plan pricing:", error);
    },
  });
}

// Utility functions for pricing calculations
export const pricingUtils = {
  // Convert paise to rupees
  paiseToRupees: (paise: number): number => paise / 100,
  
  // Convert rupees to paise
  rupeesToPaise: (rupees: number): number => Math.round(rupees * 100),
  
  // Calculate annual savings
  calculateAnnualSavings: (monthlyPaise: number, annualPaise: number): number => {
    const monthlyYearly = monthlyPaise * 12;
    return monthlyYearly - annualPaise;
  },
  
  // Calculate discount percentage
  calculateDiscountPercent: (monthlyPaise: number, annualPaise: number): number => {
    if (monthlyPaise === 0) return 0;
    const monthlyYearly = monthlyPaise * 12;
    return Math.round(((monthlyYearly - annualPaise) / monthlyYearly) * 100);
  },
  
  // Format currency display
  formatCurrency: (paise: number): string => {
    const rupees = paise / 100;
    return `₹${rupees.toLocaleString('en-IN')}`;
  },
  
  // Format credits display
  formatCredits: (credits: number): string => {
    if (credits >= 1000000) {
      return `${(credits / 1000000).toFixed(1)}M`;
    } else if (credits >= 1000) {
      return `${(credits / 1000).toFixed(0)}K`;
    }
    return credits.toLocaleString('en-IN');
  },
  
  // Validate pricing data
  validatePricingData: (data: PlanPricingUpdate): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (data.monthlyPricePaise !== undefined && data.monthlyPricePaise < 0) {
      errors.push("Monthly price cannot be negative");
    }
    
    if (data.annualPricePaise !== undefined && data.annualPricePaise < 0) {
      errors.push("Annual price cannot be negative");
    }
    
    if (data.annualDiscountPercent !== undefined && (data.annualDiscountPercent < 0 || data.annualDiscountPercent > 100)) {
      errors.push("Annual discount must be between 0 and 100");
    }
    
    if (data.monthlyCreditGrant !== undefined && data.monthlyCreditGrant < 0) {
      errors.push("Monthly credit grant cannot be negative");
    }
    
    if (data.addonCreditPrice !== undefined && data.addonCreditPrice < 0) {
      errors.push("Addon credit price cannot be negative");
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

// Hook for bulk operations
export function useBulkPlanOperations() {
  const queryClient = useQueryClient();
  const updateMutation = useUpdatePlanPricing();

  const updateMultiplePlans = async (updates: Array<{ planCode: string; pricingData: PlanPricingUpdate }>) => {
    const results = [];
    
    for (const update of updates) {
      try {
        const result = await updateMutation.mutateAsync(update);
        results.push({ planCode: update.planCode, success: true, data: result });
      } catch (error) {
        results.push({ planCode: update.planCode, success: false, error });
      }
    }
    
    return results;
  };

  return {
    updateMultiplePlans,
    isLoading: updateMutation.isPending
  };
}